import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { weekdayByIndexMap, WeekdayIndex } from '../../../helpers/constants';
import { getTenantTimeFromUTC } from '../../../helpers/date';
import { cutPastDates, fillWeeks, isOutsideOfSelectableInterval } from '../application';
import { getOnboardingAutostartActive } from '../application/getOnboardingAutostartActive';
import { OnboardingStepsService } from '../application/OnboardingStepsService';
import { createWorkItemDate, isAvailabilityDateEqual } from '../domain/AvailabilityDate';
import { OnboardingStep, WorkCalendarRecord } from '../types';
import { acceptAvailabilityThunk, loadAvailabilityRequestThunk } from './thunks';

type State = {
    expired: boolean;
    selectedCalendarRecords: WorkCalendarRecord[];
    loading: boolean;
    loadingError: string | null;
    sending: boolean;
    sendingError: string | null;
    success: boolean;
    visibleMonth: string;
    onboardingStep: OnboardingStep | null;
    onboardingAutostart?: boolean;
    replaceSelected?: boolean;
    workCalendar: WorkCalendarRecord[];
    workCalendarLoading: boolean;
    workCalendarError: string | null;
}

const initialOnboardingAutostart = getOnboardingAutostartActive();
const initialOnboardingStep = initialOnboardingAutostart && 'week';

const initialState: State = {
    selectedCalendarRecords: [],
    loading: false,
    loadingError: null,
    sending: false,
    sendingError: null,
    success: false,
    expired: false,
    visibleMonth: '',
    onboardingStep: initialOnboardingStep || null,
    onboardingAutostart: initialOnboardingAutostart,
    replaceSelected: false,
    workCalendar: [],
    workCalendarLoading: false,
    workCalendarError: null
};

export const candidateAvailabilitySlice = createSlice({
    name: 'candidateAvailability',
    initialState,
    reducers: {
        deselectAll: (state) => {
            state.selectedCalendarRecords = [];
        },
        setExpired: state => {
            state.expired = true;
        },
        toggleAll: (state, action: PayloadAction<{ timeZone: string }>) => {
            const { timeZone } = action.payload;

            const availableForSelectionDates = state.workCalendar.filter(d => !isOutsideOfSelectableInterval(state.workCalendar, d, timeZone));
            const availableForSelectionDatesCount = availableForSelectionDates.length;

            const actionType: 'select' | 'deselect' = state.selectedCalendarRecords.length < availableForSelectionDatesCount ? 'select' : 'deselect';

            if (actionType === 'select') {
                state.selectedCalendarRecords = availableForSelectionDates;
            } else {
                state.selectedCalendarRecords = [];
                state.sendingError = null;
            }

            state.replaceSelected = false;
        },
        toggleWeek: (state, action: PayloadAction<{ weekIndex: number; timeZone: string }>) => {
            const { timeZone, weekIndex } = action.payload;
            const dates = fillWeeks(cutPastDates(state.workCalendar, timeZone), timeZone);
            const weekStartIndex = weekIndex * 7;
            const week = dates.slice(weekStartIndex, weekStartIndex + 7);

            const { selectedCalendarRecords } = state;

            const someDayIsNotSelected = week.some((availabilityDate) => {
                const dayIsNotSelected = !state.selectedCalendarRecords.some((sad) => isAvailabilityDateEqual(sad, availabilityDate));
                const isOutsideOfInterval = isOutsideOfSelectableInterval(state.workCalendar, availabilityDate, timeZone);

                return dayIsNotSelected && !isOutsideOfInterval;
            });

            const actionType: 'select' | 'deselect' = someDayIsNotSelected ? 'select' : 'deselect';

            if (actionType === 'deselect') {
                state.selectedCalendarRecords = selectedCalendarRecords.filter((sad) => !week.some((weekDay) => isAvailabilityDateEqual(weekDay, sad)));
            } else {
                const resultedSelection = state.replaceSelected ? [] : state.selectedCalendarRecords.slice();

                week.forEach((weekDay) => {
                    const dayIsNotSelected = !resultedSelection.some((selectedDate) => isAvailabilityDateEqual(selectedDate, weekDay));
                    const isOutsideOfInterval = isOutsideOfSelectableInterval(state.workCalendar, weekDay, timeZone);

                    if (dayIsNotSelected && !isOutsideOfInterval) {
                        resultedSelection.push(weekDay);
                    }
                });

                state.selectedCalendarRecords = resultedSelection;
            }

            if (state.selectedCalendarRecords.length === 0) {
                state.sendingError = null;
            }

            state.replaceSelected = false;
        },
        toggleDay: (state, action: PayloadAction<WorkCalendarRecord>) => {
            const toggleDate = action.payload;

            const dateSelected = state.selectedCalendarRecords.some((selectedDate) => isAvailabilityDateEqual(selectedDate, toggleDate));

            if (!dateSelected && state.replaceSelected) {
                state.selectedCalendarRecords = [ toggleDate ];
            } else if (!dateSelected) {
                state.selectedCalendarRecords = [ ...state.selectedCalendarRecords, toggleDate ];
            } else {
                state.selectedCalendarRecords = state.selectedCalendarRecords.filter((selectedDate) => !isAvailabilityDateEqual(selectedDate, toggleDate));
            }

            if (state.selectedCalendarRecords.length === 0) {
                state.sendingError = null;
            }

            state.replaceSelected = false;
        },
        toggleWeekDay: (state, action: PayloadAction<{ weekdayIndex: WeekdayIndex; timeZone: string }>) => {
            const { timeZone, weekdayIndex } = action.payload;
            const weekday = weekdayByIndexMap[weekdayIndex];

            const days = state.workCalendar.filter((availabilityDate) => {
                const dateTz = getTenantTimeFromUTC(new Date(availabilityDate.date), timeZone);

                return dateTz.getDay() === weekday;
            });

            const someDayIsNotSelected = days.some((availabilityDate) => {
                const dayIsNotSelected = !state.selectedCalendarRecords.some((date) => isAvailabilityDateEqual(date, availabilityDate));
                const isOutsideOfInterval = isOutsideOfSelectableInterval(state.workCalendar, availabilityDate, timeZone);

                return dayIsNotSelected && !isOutsideOfInterval;
            });

            const actionType: 'select' | 'deselect' = someDayIsNotSelected ? 'select' : 'deselect';

            if (actionType === 'deselect') {
                state.selectedCalendarRecords = state.selectedCalendarRecords.filter((sad) => !days.some(day => isAvailabilityDateEqual(day, sad)));
            } else {
                const notYetSelectedDays = days.filter((day) => {
                    const isNotSelected = state.replaceSelected || !state.selectedCalendarRecords.some((sad) => isAvailabilityDateEqual(sad, day));
                    const isOutsideOfInterval = isOutsideOfSelectableInterval(state.workCalendar, day, timeZone);

                    return isNotSelected && !isOutsideOfInterval;
                });

                const prevSelectedDays = state.replaceSelected ? [] : state.selectedCalendarRecords;
                state.selectedCalendarRecords = prevSelectedDays.concat(notYetSelectedDays);
            }

            if (state.selectedCalendarRecords.length === 0) {
                state.sendingError = null;
            }

            state.replaceSelected = false;
        },
        setVisibleMonth: (state, action: PayloadAction<string>) => {
            state.visibleMonth = action.payload;
        },
        nextOnboardingStep: (state) => {
            state.onboardingStep = OnboardingStepsService.getNextStep(state.onboardingStep);
        },
        stopOnboarding: (state) => {
            state.onboardingStep = null;
        },
        setOnboardingAutostart: (state, action: PayloadAction<boolean>) => {
            state.onboardingAutostart = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadAvailabilityRequestThunk.pending, (state) => {
            state.loading = true;
            state.loadingError = null;
        });

        builder.addCase(loadAvailabilityRequestThunk.rejected, (state, { payload: errorCode }) => {
            state.loadingError = errorCode ?? null;
            state.loading = false;
        });

        builder.addCase(loadAvailabilityRequestThunk.fulfilled, (state, { payload }) => {
            state.workCalendar = payload.workCalendar;
            state.loading = false;
            state.workCalendarLoading = false;
        });

        builder.addCase(acceptAvailabilityThunk.pending, (state) => {
            state.sending = true;
            state.sendingError = null;
        });

        builder.addCase(acceptAvailabilityThunk.rejected, (state, { payload }) => {
            state.sendingError = payload ?? null;
            state.sending = false;
        });

        builder.addCase(acceptAvailabilityThunk.fulfilled, (state, { meta, payload }) => {
            state.sending = false;
            state.sendingError = null;
            state.selectedCalendarRecords = [];
            const updatedDates = new Set(
                meta.arg
                    .map((date) => new Date(date.date).getTime()) // dates which were updated by user
                    .concat(payload.map((date) => new Date(date.date).getTime())) // all returned dates from BE. Is needed because of overnight availabilities which are extendend to the next day
            );

            state.workCalendar = state.workCalendar.map((workRecord) => {
                const dateToCompare = new Date(workRecord.date).getTime();

                if (updatedDates.has(dateToCompare)) {
                    const availabilityRecord = payload.find((availabilityRecord) => new Date(availabilityRecord.date).getTime() === dateToCompare);

                    return createWorkItemDate(new Date(workRecord.date), {
                        ...(availabilityRecord || {}),
                        orders: workRecord.orders
                    });
                }

                return workRecord;
            });
        });
    }
});

export const {
    setExpired,
    setVisibleMonth,
    deselectAll,
    toggleAll,
    toggleDay,
    toggleWeek,
    toggleWeekDay,
    nextOnboardingStep,
    stopOnboarding,
    setOnboardingAutostart
} = candidateAvailabilitySlice.actions;

export default candidateAvailabilitySlice.reducer;
